// Rückfrage „Ungespeicherte Änderungen" vor dem Verlassen der Begehung.
//
// Die Erfassung im Portal hat kein Autosave: Prüfpunkte und Mängel liegen bis
// „Zwischenspeichern" nur im State von FreelancerInspection. Verlassen wird
// sie auf zwei Wegen — über „Meine Aufträge" in der Maske selbst und über den
// Kopf des Portals (FreelancerPortal: die Reiter über `setBereich`, dazu
// „Abmelden"). Den ersten fängt die Maske selbst ab. Für den zweiten meldet
// sie sich hier an, solange etwas offen ist; das Portal geht dann über
// `begehungVerlassen`.
//
// Dasselbe Muster wie registerBauphaseFlusher und registriereZurueck: eine
// Anmeldung, die beim Abbau genau sich selbst wieder abmeldet.

/** Fragt nach und ruft `weiter` erst, wenn die Begehung verlassen werden darf. */
export type VerlassenWache = (weiter: () => void) => void;

let wache: VerlassenWache | null = null;

/** Meldet die Wache an; die Rückgabe meldet genau diese wieder ab. */
export const registriereVerlassenWache = (neu: VerlassenWache): (() => void) => {
    wache = neu;
    return () => {
        // Eine spätere Anmeldung (Remount, StrictMode) bleibt stehen.
        if (wache === neu) wache = null;
    };
};

/** Die Begehung verlassen: ohne angemeldete Wache sofort, sonst nach der Rückfrage. */
export const begehungVerlassen = (weiter: () => void): void => {
    if (wache) wache(weiter);
    else weiter();
};

/**
 * Nachgefragt wird nur, wenn es etwas zu verlieren gibt UND es sich speichern
 * ließe. Ist der Auftrag nicht mehr in Bearbeitung, gibt es keinen
 * Speicherweg — die Rückfrage hätte dann nur „Verwerfen" anzubieten.
 * Ein laufender Foto-Upload zählt mit: ohne ihn fehlte das Foto im Mangel.
 */
export const rueckfrageVorVerlassen = (
    ungespeichert: boolean,
    bearbeitbar: boolean,
    fotosLaufen = 0,
): boolean => (ungespeichert || fotosLaufen > 0) && bearbeitbar;

// ─── Änderungen während des Speicherns ───────────────────────────────────────
// Die Nutzlast entsteht beim Start des Speicherns. Was während des Wartens
// dazukommt (Eingabe, fertiger Foto-Upload), war nicht darin. Jede Änderung
// zählt deshalb einen Stand hoch; als gespeichert gilt nur, was beim Start
// schon der letzte Stand war.

/** Kam seit dem Start des Speicherns nichts dazu? Nur dann gilt alles als gespeichert. */
export const nichtsDazugekommen = (standBeimStart: number, standJetzt: number): boolean =>
    standBeimStart === standJetzt;

/** Was der letzte Speicherversuch aus der Leiste ergab; null = noch keiner. */
export type LeistenErgebnis = 'fehlgeschlagen' | 'geaendert' | null;

/** Nach „Speichern" in der Leiste: weiter nur, wenn gespeichert UND nichts dazukam. */
export const ausgangNachSpeichern = (
    gespeichert: boolean,
    standBeimStart: number,
    standJetzt: number,
): 'weiter' | 'fehlgeschlagen' | 'geaendert' => {
    if (!gespeichert) return 'fehlgeschlagen';
    return nichtsDazugekommen(standBeimStart, standJetzt) ? 'weiter' : 'geaendert';
};

export interface VerlassenLeiste {
    titel: string;
    text: string;
    /** Der Grund des Fehlschlags, wenn es einen gibt — sonst null. */
    grund: string | null;
    /** Noch etwas offen? Sonst heißt der rechte Knopf „Weiter". */
    nochOffen: boolean;
}

/**
 * Was die Leiste sagt — nach dem JETZIGEN Stand, nicht nach dem beim Öffnen.
 * Endet ein „Zwischenspeichern", während sie steht, sagt sie das, statt weiter
 * von ungespeicherten Änderungen zu sprechen.
 */
export const verlassenLeiste = (
    ergebnis: LeistenErgebnis,
    ungespeichert: boolean,
    fotosLaufen: number,
    fehler: string | null,
): VerlassenLeiste => {
    const titel = 'Ungespeicherte Änderungen';
    if (fotosLaufen > 0) {
        return { titel, text: 'Foto wird noch hochgeladen …', grund: null, nochOffen: true };
    }
    if (!ungespeichert) {
        return {
            titel: 'Alles gespeichert',
            text: 'Die Begehung ist inzwischen gespeichert.',
            grund: null,
            nochOffen: false,
        };
    }
    if (ergebnis === 'fehlgeschlagen') {
        return {
            titel,
            text: 'Speichern hat nicht geklappt – bitte erneut versuchen oder verwerfen.',
            grund: fehler,
            nochOffen: true,
        };
    }
    if (ergebnis === 'geaendert') {
        return {
            titel,
            text: 'Während des Speicherns kam noch etwas dazu – bitte erneut speichern.',
            grund: null,
            nochOffen: true,
        };
    }
    return {
        titel,
        text: 'Prüfpunkte oder Mängel dieser Begehung sind noch nicht gespeichert.',
        grund: null,
        nochOffen: true,
    };
};
